import React from 'react';
import { PageHero } from '../../components/ui/PageHero';
import { QuoteBanner } from '../../components/ui/QuoteBanner';
import { ReviewNote } from '../../components/ui/ReviewNote';
import { SectionHeading } from '../../components/ui/SectionHeading';
import { ButtonLink } from '../../components/ui/ButtonLink';
import { useSeo } from '../../hooks/useSeo';
import { pageMeta } from '../../data/seo';
import { companyRegistration, registrationGroups } from '../../data/registrations';
import { images } from '../../data/images';
export function RegistrationsProcurement() {
  useSeo(pageMeta.registrations);
  return <>
      <PageHero title="Registrations & procurement" intro="Company registration and procurement codes for tender evaluators and procurement teams." image={images.paving} breadcrumbs={[{
      label: 'Home',
      to: '/'
    }, {
      label: 'About Us'
    }, {
      label: 'Registrations & Procurement'
    }]} data-id="element-643" data-mp="2x4q749vuudz9" />

      <section aria-labelledby="company-reg-title" className="bg-charcoal py-20 md:py-24" data-id="element-644" data-mp="3kuaqgtvx7d92">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-645" data-mp="2fooeeprhj3fr">
          <SectionHeading id="company-reg-title" label="Company" title="Company registration" data-id="element-646" data-mp="2jpq3idrotz40" />
          <dl className="mt-10 grid border-t border-white/15 sm:grid-cols-2 lg:grid-cols-3" data-id="element-647" data-mp="25yl9psb0411l">
            {companyRegistration.map(row => <div key={row.label} className="border-b border-white/15 py-5 sm:pr-8" data-id="element-648" data-mp="2avbswoi3rcr8">
                <dt className="text-[12px] font-semibold uppercase tracking-[0.14em] text-steel-400" data-id="element-649" data-mp="1vycccb59w9km">{row.label}</dt>
                <dd className="mt-1 font-display text-[22px] font-bold uppercase text-white" data-id="element-650" data-mp="20wvz2droxpe1">{row.value}</dd>
              </div>)}
          </dl>
        </div>
      </section>

      <section aria-labelledby="procurement-title" className="bg-charcoal-950 py-20 md:py-24" data-id="element-651" data-mp="1ryu167397cuz">
        <div className="mx-auto max-w-site px-5 md:px-8" data-id="element-652" data-mp="3hdrvr9qndxo5">
          <SectionHeading id="procurement-title" label="Procurement" title="Procurement codes and grades" intro="The profile states that the company is registered with the Public Procurement and Asset Disposal Board (PPADB) and with Debswana Diamond Company, under the codes below." className="max-w-3xl" data-id="element-653" data-mp="3mcv1kwegc7x6" />

          <div className="mt-12 space-y-14" data-id="element-654" data-mp="35xvk61uff6sm">
            {registrationGroups.map(group => <div key={group.authority} data-id="element-655" data-mp="7g9tkvcd3npp">
                <div className="flex flex-col gap-1 md:flex-row md:items-end md:justify-between" data-id="element-656" data-mp="7f86ttlzhcs5">
                  <h3 className="font-display text-[30px] font-extrabold uppercase text-white" data-id="element-657" data-mp="2i80dhl8280i">{group.authority}</h3>
                  <p className="text-sm text-steel-400" data-id="element-658" data-mp="3tu9np4o7s2ez">{group.description}</p>
                </div>
                <div className="mt-4 overflow-x-auto" data-id="element-659" data-mp="3ow85sn2iy953">
                  <table className="w-full min-w-[560px] text-left" data-id="element-660" data-mp="q79cbet9k1aj">
                    <caption className="sr-only" data-id="element-661" data-mp="q8kg3empl91n">{group.authority} registration codes and grades</caption>
                    <thead data-id="element-662" data-mp="le7x15p59zqf">
                      <tr className="border-b-2 border-brand-red text-[12px] uppercase tracking-[0.14em] text-steel-400" data-id="element-663" data-mp="srpa5l61mj22">
                        <th scope="col" className="py-3 pr-6 font-semibold" data-id="element-664" data-mp="xr1rxri24zxs">Code</th>
                        <th scope="col" className="py-3 pr-6 font-semibold" data-id="element-665" data-mp="12p3kd31diblx">Sub-code / category</th>
                        <th scope="col" className="py-3 text-right font-semibold" data-id="element-666" data-mp="17opxzdl45tv9">Grade</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-667" data-mp="1bnysiknz9vrv">
                      {group.rows.map(row => <tr key={`${row.code}-${row.subCode}`} className="border-b border-white/10" data-id="element-668" data-mp="1gnjtt1qmeg7r">
                          <td className="py-4 pr-6 text-[15px] text-steel-300" data-id="element-669" data-mp="1lgcv1fmvr6f3">{row.code}</td>
                          <th scope="row" className="py-4 pr-6 text-[16px] font-semibold text-white" data-id="element-670" data-mp="1qeehmajilnvb">
                            {row.subCode}
                          </th>
                          <td className="py-4 text-right font-display text-[22px] font-bold uppercase text-white" data-id="element-671" data-mp="1zgnpstrb955j">{row.grade}</td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>
              </div>)}
          </div>

          <ReviewNote className="mt-10" data-id="element-672" data-mp="35vr9bs1ebrci">
            These grades are copied from the October 2026 profile. Before presenting them as current, check them against
            the valid PPADB and Debswana certificates and add expiry dates if you want them shown. The profile cover says
            "Grade E–Unlimited", which is not stated in the tables, and the grouping of the "Roads and Civil Works" row in
            the source table is unclear. Please confirm both.
          </ReviewNote>

          <div className="mt-12 flex flex-col gap-6 border-t-[3px] border-brand-red bg-charcoal-900 p-8 md:flex-row md:items-center md:justify-between" data-id="element-673" data-mp="2i6yha6nwa3n6">
            <div data-id="element-674" data-mp="2hwxqrigdkui">
              <h3 className="font-display text-[26px] font-bold uppercase text-white" data-id="element-675" data-mp="3tmn6l4omfer5">Need registration documents for a tender?</h3>
              <p className="mt-2 max-w-xl text-[15px] text-steel-300" data-id="element-676" data-mp="5wyck6xtl9df">
                Ask the team for copies of company registration and procurement certificates for your evaluation.
              </p>
            </div>
            <ButtonLink to="/contact#enquiry" showArrow className="self-start md:self-auto" data-id="element-677" data-mp="3s6sop6nt3rqm">
              Contact the Team
            </ButtonLink>
          </div>
        </div>
      </section>

      <QuoteBanner data-id="element-678" data-mp="op10828vppx6" />
    </>;
}